import { useTranslation } from 'react-i18next';
import type { Project } from '@jisy/shared';
import { formatRelative } from '../lib/format.js';
import { SectionMark } from './Badges.js';
import styles from './Sidebar.module.css';

/** Режимы работы, между которыми переключает боковая колонка. */
export type AppView = 'table' | 'planning' | 'meetings';

/**
 * Боковая колонка: сверху выбор режима, ниже — разделы, внизу свежесть данных.
 *
 * Режим стоит в колонке, а не в тулбаре: тулбар принадлежит содержимому и целиком меняется
 * вместе с ним, а переключатель обязан оставаться на одном месте в обоих режимах.
 * Разделы показываются только в таблице — план собирается из задач любых разделов,
 * и фильтром они там не работают.
 */
export function Sidebar({
  view,
  onChangeView,
  projects,
  totalIssues,
  selectedProjectId,
  onSelect,
  lastSyncAt,
  syncing,
  onSyncNow,
  onCreateSection,
  collapsed,
  onToggleCollapsed,
}: {
  view: AppView;
  onChangeView: (view: AppView) => void;
  projects: Project[];
  totalIssues: number;
  selectedProjectId: string | null;
  onSelect: (projectId: string | null) => void;
  lastSyncAt: string | null;
  syncing: boolean;
  onSyncNow: () => void;
  /** Открыть форму нового раздела. Создаётся он в Jira, поэтому окно живёт на странице. */
  onCreateSection: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}): React.ReactElement {
  const { t } = useTranslation();

  if (collapsed) {
    return (
      <nav className={styles.collapsed} aria-label={t('nav.title')}>
        <button
          type="button"
          className={styles.collapseButton}
          onClick={onToggleCollapsed}
          title={t('sections.expand')}
          aria-label={t('sections.expand')}
        >
          ›
        </button>

        {/* Свёрнутая колонка остаётся навигацией: режим переключается и здесь, значками. */}
        <button
          type="button"
          className={view === 'table' ? styles.iconItemActive : styles.iconItem}
          onClick={() => onChangeView('table')}
          title={t('nav.table')}
          aria-label={t('nav.table')}
          aria-current={view === 'table'}
        >
          <TableIcon />
        </button>
        <button
          type="button"
          className={view === 'planning' ? styles.iconItemActive : styles.iconItem}
          onClick={() => onChangeView('planning')}
          title={t('nav.planning')}
          aria-label={t('nav.planning')}
          aria-current={view === 'planning'}
        >
          <PlanIcon />
        </button>
        <button
          type="button"
          className={view === 'meetings' ? styles.iconItemActive : styles.iconItem}
          onClick={() => onChangeView('meetings')}
          title={t('nav.meetings')}
          aria-label={t('nav.meetings')}
          aria-current={view === 'meetings'}
        >
          <MeetingIcon />
        </button>
      </nav>
    );
  }

  return (
    <nav className={styles.sidebar} aria-label={t('nav.title')}>
      <div className={styles.head}>
        <button
          type="button"
          className={styles.collapseButton}
          onClick={onToggleCollapsed}
          title={t('sections.collapse')}
          aria-label={t('sections.collapse')}
        >
          ‹
        </button>
      </div>

      <div className={styles.nav}>
        <button
          type="button"
          className={view === 'table' ? styles.navItemActive : styles.navItem}
          onClick={() => onChangeView('table')}
          aria-current={view === 'table'}
        >
          <TableIcon />
          <span className={styles.navName}>{t('nav.table')}</span>
        </button>
        <button
          type="button"
          className={view === 'planning' ? styles.navItemActive : styles.navItem}
          onClick={() => onChangeView('planning')}
          aria-current={view === 'planning'}
        >
          <PlanIcon />
          <span className={styles.navName}>{t('nav.planning')}</span>
        </button>
        <button
          type="button"
          className={view === 'meetings' ? styles.navItemActive : styles.navItem}
          onClick={() => onChangeView('meetings')}
          aria-current={view === 'meetings'}
        >
          <MeetingIcon />
          <span className={styles.navName}>{t('nav.meetings')}</span>
        </button>
      </div>

      {view === 'table' && (
        <>
          <div className={styles.sectionsHead}>
            <span className="label">{t('sections.title')}</span>
            {/*
              Новый раздел заводится здесь же, где выбирается: список разделов — единственное
              место, где вообще видно, каких направлений не хватает.
            */}
            <button
              type="button"
              className={styles.addSection}
              onClick={onCreateSection}
              title={t('sections.create')}
              aria-label={t('sections.create')}
            >
              +
            </button>
          </div>

          <div className={styles.list}>
            <button
              type="button"
              className={`${styles.item} ${selectedProjectId === null ? styles.itemActive : ''}`}
              onClick={() => onSelect(null)}
              aria-current={selectedProjectId === null}
            >
              <span className={styles.itemMarkPlaceholder} aria-hidden="true" />
              <span className={`${styles.itemName} truncate`}>{t('sections.all')}</span>
              <span className={`mono ${styles.itemCount}`}>{totalIssues}</span>
            </button>

            {projects.map((project) => (
              <button
                type="button"
                key={project.id}
                className={`${styles.item} ${selectedProjectId === project.id ? styles.itemActive : ''}`}
                onClick={() => onSelect(project.id)}
                aria-current={selectedProjectId === project.id}
                title={`${project.key} · ${project.name}`}
              >
                <SectionMark colorIndex={project.colorIndex} />
                <span className={`${styles.itemName} truncate`}>{project.name}</span>
                <span className={`mono ${styles.itemCount}`}>{project.issueCount ?? 0}</span>
              </button>
            ))}
          </div>
        </>
      )}

      <div className={styles.footer}>
        <span className={styles.footerText}>
          {syncing
            ? t('sections.syncing')
            : t('sections.syncedAt', { time: formatRelative(lastSyncAt, t) })}
        </span>
        {/* Синхронизация идёт по обращению к данным, а не по расписанию: когда в Jira
            что-то поменяли только что, ждать истечения TTL незачем. */}
        <button
          type="button"
          className={styles.syncButton}
          onClick={onSyncNow}
          disabled={syncing}
          title={t('sections.syncNowHint')}
          aria-label={t('sections.syncNow')}
        >
          <span aria-hidden="true" className={syncing ? styles.spinner : undefined}>
            ↻
          </span>
        </button>
      </div>
    </nav>
  );
}

/** Таблица: шапка и колонка. */
function TableIcon(): React.ReactElement {
  return (
    <svg
      className={styles.icon}
      viewBox="0 0 16 16"
      width="14"
      height="14"
      aria-hidden="true"
      focusable="false"
    >
      <rect
        x="2.5"
        y="3.5"
        width="11"
        height="9"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <path d="M2.5 6.5h11M6.5 6.5v6" fill="none" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

/** Встречи: камера записи — то, чем встреча попадает в Jisy. */
function MeetingIcon(): React.ReactElement {
  return (
    <svg
      className={styles.icon}
      viewBox="0 0 16 16"
      width="14"
      height="14"
      aria-hidden="true"
      focusable="false"
    >
      <rect
        x="2"
        y="4.5"
        width="8"
        height="7"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <path
        d="M10 7.5l3.5-2v5l-3.5-2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Планирование: три отрезка со сдвигом — то же, что видно в сетке. */
function PlanIcon(): React.ReactElement {
  return (
    <svg
      className={styles.icon}
      viewBox="0 0 16 16"
      width="14"
      height="14"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M3 4.5h6M6 8h7M3 11.5h5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}
